/**
 * Centralized date formatting utilities for ALL apps in the monorepo.
 *
 * All apps use the same formatters, but:
 * - Organization app: Passes organization-specific timezone as parameter
 * - All other apps: Use formatters without timezone (defaults to Copenhagen)
 */

const DEFAULT_TIMEZONE = 'Europe/Copenhagen';

/**
 * Generic date formatter that accepts an optional timezone parameter
 */
function formatDate(
	date: string | Date | null | undefined,
	options: Intl.DateTimeFormatOptions = {},
	timezone?: string | null,
	locale: string = 'en-US'
): string {
	if (!date) return '-';

	const dateObj = typeof date === 'string' ? new Date(date) : date;

	if (isNaN(dateObj.getTime())) {
		return 'Invalid Date';
	}

	const formatOptions: Intl.DateTimeFormatOptions = {
		...options,
		timeZone: timezone || DEFAULT_TIMEZONE,
	};

	try {
		return dateObj.toLocaleString(locale, formatOptions);
	} catch {
		// Fallback to Copenhagen if timezone is invalid
		// Removed console.warn
		const fallbackOptions = { ...options, timeZone: DEFAULT_TIMEZONE };
		return dateObj.toLocaleString(locale, fallbackOptions);
	}
}

/**
 * Universal date formatting functions - used by ALL apps
 * Optional timezone parameter allows organization app to pass specific timezone
 */

export function formatDateDefault(
	date: string | Date | null | undefined,
	timezone?: string | null
): string {
	return formatDate(
		date,
		{
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		},
		timezone
	);
}

export function formatDateTime(
	date: string | Date | null | undefined,
	timezone?: string | null
): string {
	return formatDate(
		date,
		{
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		},
		timezone
	);
}

export function formatDateLong(
	date: string | Date | null | undefined,
	timezone?: string | null
): string {
	return formatDate(
		date,
		{
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		},
		timezone
	);
}

export function formatDateShort(
	date: string | Date | null | undefined,
	timezone?: string | null
): string {
	return formatDate(
		date,
		{
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		},
		timezone
	);
}

export function formatTime(
	date: string | Date | null | undefined,
	timezone?: string | null
): string {
	return formatDate(
		date,
		{
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		},
		timezone
	);
}

export function formatDateTimeFull(
	date: string | Date | null | undefined,
	timezone?: string | null
): string {
	return formatDate(
		date,
		{
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			timeZoneName: 'short',
			hour12: false,
		},
		timezone
	);
}

export function formatDateForTable(
	date: string | Date | null | undefined,
	includeTime = false,
	timezone?: string | null
): string {
	if (includeTime) {
		return formatDate(
			date,
			{
				month: 'short',
				day: 'numeric',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				hour12: false,
			},
			timezone
		);
	}
	return formatDate(
		date,
		{
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		},
		timezone
	);
}
